import { isNearLatest, shouldFollowLatest } from "@/src/lib/conversationScroll";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { FlatList, NativeScrollEvent, NativeSyntheticEvent } from "react-native";

export function useConversationScroll<Message>(contextKey: string, latestMessageId: string | undefined, inverted = true) {
  const listRef = useRef<FlatList<Message>>(null);
  const nearLatestRef = useRef(true);
  const previousLatestIdRef = useRef<string | undefined>(undefined);
  const pendingFollowRef = useRef(false);
  const [showReturnToLatest, setShowReturnToLatest] = useState(false);

  useEffect(() => {
    nearLatestRef.current = true;
    previousLatestIdRef.current = undefined;
    pendingFollowRef.current = false;
    setShowReturnToLatest(false);
  }, [contextKey]);

  const followLatest = useCallback((sentByCurrentIdentity = false) => {
    if (shouldFollowLatest(nearLatestRef.current, sentByCurrentIdentity)) {
      pendingFollowRef.current = true;
    }
  }, []);

  useLayoutEffect(() => {
    if (!latestMessageId || latestMessageId === previousLatestIdRef.current) return;
    if (previousLatestIdRef.current === undefined) {
      // Web keeps a chronological list, so its initial content must scroll to the end.
      nearLatestRef.current = true;
      if (!inverted) pendingFollowRef.current = true;
    } else {
      followLatest();
    }
    previousLatestIdRef.current = latestMessageId;
  }, [followLatest, inverted, latestMessageId]);

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const distanceFromLatest = inverted
      ? contentOffset.y
      : contentSize.height - layoutMeasurement.height - contentOffset.y;
    const nearLatest = isNearLatest(Math.max(0, distanceFromLatest));
    nearLatestRef.current = nearLatest;
    setShowReturnToLatest(!nearLatest);
  }, [inverted]);

  const scrollToLatest = useCallback((animated: boolean) => {
    if (inverted) listRef.current?.scrollToOffset({ offset: 0, animated });
    else listRef.current?.scrollToEnd({ animated });
  }, [inverted]);

  const onContentSizeChange = useCallback(() => {
    if (!pendingFollowRef.current) return;
    pendingFollowRef.current = false;
    // Wait for FlatList's native content-position adjustment after the new row is laid out.
    requestAnimationFrame(() => scrollToLatest(false));
  }, [scrollToLatest]);

  const cancelFollow = useCallback(() => {
    pendingFollowRef.current = false;
  }, []);

  const onLayout = useCallback(() => {
    if (nearLatestRef.current) {
      requestAnimationFrame(() => scrollToLatest(false));
    }
  }, [scrollToLatest]);

  const returnToLatest = useCallback(() => {
    pendingFollowRef.current = false;
    scrollToLatest(true);
  }, [scrollToLatest]);

  return { listRef, onScroll, onLayout, onContentSizeChange, returnToLatest, showReturnToLatest, followLatest, cancelFollow };
}
